<main class="login-container" [class.admin-mode]="isAdminMode">
  <!-- Back to Home Floating Navigation -->
  <a [routerLink]="homeLink" class="btn-back-home" [attr.aria-label]="resolvedHomeLinkText">
    <i class="ri-arrow-left-line"></i>
    <span>{{ resolvedHomeLinkText }}</span>
  </a>

  <!-- Left: Hero / Cinema Backdrop Collage -->
  <section #searchContainer class="collage-container" [class.admin-collage]="isAdminMode">
    <div #bgSearchOverlay class="bg-collage-overlay"></div>
    <div class="collage-content">
      <div class="header-container" #headerContainer>
        <h2 class="header">{{ resolvedHeroTitle }}</h2>
        <p class="sub-header" #subHeader>{{ resolvedHeroSubtitle }}</p>
      </div>
    </div>
  </section>

  <!-- Right: Form Section -->
  <section class="form-section" [class.admin-form]="isAdminMode">
    <header class="form-header">
      <figure class="form-logo">
        <img src="/assets/images/icon.png" height="64" alt="Scenetale Logo">
      </figure>
      <h1 class="form-title">{{ resolvedHeroTitle }}</h1>
    </header>

    <!-- Error / Success Notices -->
    <div *ngIf="errorMessage" class="alert-banner error" role="alert" aria-live="polite">
      <i class="ri-error-warning-line"></i>
      <span>{{ errorMessage }}</span>
    </div>
    <div *ngIf="successMessage" class="alert-banner success" role="status" aria-live="polite">
      <i class="ri-checkbox-circle-line"></i>
      <span>{{ successMessage }}</span>
    </div>

    <!-- Semantic Form Container -->
    <form class="auth-form-shell" (ngSubmit)="isLoginMode ? submitCredentials() : submitSignup()" novalidate>

      <!-- LOGIN MODE FIELDS -->
      <div *ngIf="isLoginMode" class="form-fields-group">
        <div class="form-field">
          <label for="auth-login-email" class="form-label">
            {{ languageService.t('email') }} <span class="required">*</span>
          </label>
          <div class="input-wrapper">
            <i class="input-icon ri-mail-line" aria-hidden="true"></i>
            <input id="auth-login-email" type="email" name="email" [placeholder]="languageService.t('emailPlaceholder')"
              [(ngModel)]="email" [disabled]="isSubmitting" autocomplete="email" required enterkeyhint="next">
          </div>
        </div>

        <div class="form-field">
          <label for="auth-login-password" class="form-label">
            {{ languageService.t('password') }} <span class="required">*</span>
          </label>
          <div class="input-wrapper">
            <i class="input-icon ri-lock-2-line" aria-hidden="true"></i>
            <input id="auth-login-password" [type]="showPassword ? 'text' : 'password'" name="password"
              [placeholder]="languageService.t('passwordPlaceholder')" [(ngModel)]="password" [disabled]="isSubmitting"
              autocomplete="current-password" required enterkeyhint="go">
            <button type="button" class="btn-toggle-eye" (click)="togglePasswordVisibility()"
              [attr.aria-label]="showPassword ? languageService.t('hidePassword') : languageService.t('showPassword')">
              <i [class]="showPassword ? 'ri-eye-off-line' : 'ri-eye-line'"></i>
            </button>
          </div>
        </div>

        <div class="form-submit-row">
          <button class="btn-submit" type="submit" [disabled]="isSubmitting">
            <span *ngIf="isSubmitting" class="submit-spinner" aria-hidden="true"></span>
            <span>{{ isSubmitting ? languageService.t('loggingIn') : languageService.t('signIn') }}</span>
          </button>
        </div>
      </div>

      <!-- SIGN UP MODE FIELDS -->
      <div *ngIf="!isLoginMode" class="form-fields-group">
        <div class="form-field">
          <label for="auth-reg-username" class="form-label">
            {{ languageService.t('username') }} <span class="required">*</span>
          </label>
          <div class="input-wrapper" [class.is-valid]="usernameStatus === 'available'"
            [class.is-invalid]="usernameStatus === 'taken' || usernameStatus === 'invalid'">
            <i class="input-icon ri-user-line" aria-hidden="true"></i>
            <input id="auth-reg-username" type="text" name="username"
              [placeholder]="languageService.t('usernamePlaceholder')" [ngModel]="username"
              (ngModelChange)="onUsernameChange($event)" [disabled]="isSubmitting" autocomplete="username" required
              minlength="3" maxlength="30" enterkeyhint="next">

            <span *ngIf="isCheckingUsername" class="input-indicator checking" title="Checking availability...">
              <span class="indicator-spinner"></span>
            </span>
            <span *ngIf="!isCheckingUsername && usernameStatus === 'available'" class="input-indicator available"
              title="Username available">
              <i class="ri-checkbox-circle-fill"></i>
            </span>
            <span *ngIf="!isCheckingUsername && (usernameStatus === 'taken' || usernameStatus === 'invalid')"
              class="input-indicator taken" title="Unavailable">
              <i class="ri-close-circle-fill"></i>
            </span>
          </div>

          <!-- Realtime Unique Username Feedback & Profile Route Preview -->
          <div class="username-feedback-area">
            <p *ngIf="usernameFeedback && (usernameStatus === 'taken' || usernameStatus === 'invalid')"
              class="field-feedback error" role="alert">
              <i class="ri-error-warning-fill"></i>
              <span>{{ usernameFeedback }}</span>
            </p>
            <p *ngIf="usernameStatus === 'available'" class="field-feedback success">
              <i class="ri-check-line"></i>
              <span>{{ usernameFeedback }} Profile: <code>/u/{{ username.trim().toLowerCase() }}</code></span>
            </p>
            <p *ngIf="usernameStatus === 'idle' || usernameStatus === 'checking'" class="field-hint">
              Your profile will be at:
              <code>/u/{{ (username.trim() ? username.trim().toLowerCase() : 'username') }}</code>
            </p>
          </div>
        </div>

        <div class="form-field">
          <label for="auth-reg-email" class="form-label">
            {{ languageService.t('email') }} <span class="required">*</span>
          </label>
          <div class="input-wrapper">
            <i class="input-icon ri-mail-line" aria-hidden="true"></i>
            <input id="auth-reg-email" type="email" name="email" [placeholder]="languageService.t('emailPlaceholder')"
              [(ngModel)]="email" [disabled]="isSubmitting" autocomplete="email" required enterkeyhint="next">
          </div>
        </div>

        <div class="form-field">
          <label for="auth-reg-password" class="form-label">
            {{ languageService.t('password') }} <span class="required">*</span>
          </label>
          <div class="input-wrapper">
            <i class="input-icon ri-lock-2-line" aria-hidden="true"></i>
            <input id="auth-reg-password" [type]="showPassword ? 'text' : 'password'" name="password"
              [placeholder]="languageService.t('passwordSignupPlaceholder')" [(ngModel)]="password"
              [disabled]="isSubmitting" autocomplete="new-password" required minlength="8" enterkeyhint="done">
            <button type="button" class="btn-toggle-eye" (click)="togglePasswordVisibility()"
              [attr.aria-label]="showPassword ? languageService.t('hidePassword') : languageService.t('showPassword')">
              <i [class]="showPassword ? 'ri-eye-off-line' : 'ri-eye-line'"></i>
            </button>
          </div>
        </div>

        <div class="form-submit-row">
          <button class="btn-submit" type="submit" [disabled]="isSubmitting || isCheckingUsername">
            <span *ngIf="isSubmitting" class="submit-spinner" aria-hidden="true"></span>
            <span>{{ isSubmitting ? languageService.t('creatingAccount') : languageService.t('signUp') }}</span>
          </button>
        </div>
      </div>
    </form>

    <!-- Social Authentication Options (Strictly disabled for Admin login) -->
    <ng-container *ngIf="!isAdminMode">
      <div class="auth-divider">
        <span class="divider-line"></span>
        <span class="divider-text">{{ languageService.t('orContinueWith') }}</span>
        <span class="divider-line"></span>
      </div>

      <div class="oAuth-icons">
        <button type="button" class="oauth-btn google-btn" title="Google" aria-label="Sign in with Google"
          (click)="login('google')">
          <i class="ri-google-fill"></i>
        </button>
        <button type="button" class="oauth-btn github-btn" title="GitHub" aria-label="Sign in with GitHub"
          (click)="login('github')">
          <i class="ri-github-fill"></i>
        </button>
        <!-- X (Twitter) login option disabled / commented out as not in use
        <button type="button" class="oauth-btn twitter-btn" title="X (Twitter)" aria-label="Sign in with X"
          (click)="login('x')">
          <i class="ri-twitter-x-fill"></i>
        </button>
        -->
        <button type="button" class="oauth-btn reddit-btn" title="Reddit" aria-label="Sign in with Reddit"
          (click)="login('reddit')">
          <i class="ri-reddit-line"></i>
        </button>
      </div>
    </ng-container>

    <!-- Mode Switcher (Sign in <-> Sign up) -->
    <div *ngIf="allowSignUp" class="mode-switch">
      <span>{{ isLoginMode ? languageService.t('dontHaveAccount') : languageService.t('alreadyHaveAccount') }}</span>
      <button type="button" class="btn-mode-toggle" (click)="changeMode()">
        {{ isLoginMode ? languageService.t('signUp') : languageService.t('signIn') }}
      </button>
    </div>

    <!-- Admin Security Notice -->
    <div *ngIf="isAdminMode" class="admin-notice">
      <i class="ri-shield-keyhole-line"></i>
      <span>{{ languageService.t('adminNote') }}</span>
    </div>
  </section>
</main>